import { Component, inject, OnInit } from '@angular/core';
import { PASSWORD_REQUIREMENTS, SIGNUP_POINTS } from './signup.const';
import { InputComponent } from '@shared/components/input/input.component';
import { BUTTON_SIZE, BUTTON_TYPES } from '@shared/components/button/button-types.enum';
import { ButtonComponent } from '@shared/components/button/button.component';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  EMAIL_INPUT_REGEX,
  emailFormat,
  matchValidator,
  NAME_REGEX,
  nonDeloitteEmail,
  notEmpty,
  PASTE_EMAIL_REGEX,
  PASTE_NAME_REGEX,
} from '../validators.const';
import { AuthenticationService } from '../../../services/authentication.service';
import { Router } from '@angular/router';
import { VerificationModalComponent } from '../verification-modal/verification-modal.component';
import { Dialog } from '@angular/cdk/dialog';
import { IHome_FranchisesResponse } from '@shared/constants/models.const';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ContentService } from '../../../services/content.service';
import { PasswordValidationComponent } from '@shared/components/password-validation/password-validation.component';
import { FranchiseInputComponent, IFranchiseItem } from '@shared/components/franchise-input/franchise-input.component';
import { MessageToasterComponent, ToasterType } from '@shared/components/message-toaster/message-toaster.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LoginModalComponent } from '../login-modal/login-modal.component';
import { take } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';

export interface IPasswordValidation {
  valid: boolean;
  text: string;
  validation: string;
}

@UntilDestroy()
@Component({
  selector: 'app-signup',
  imports: [
    InputComponent,
    ButtonComponent,
    ReactiveFormsModule,
    PasswordValidationComponent,
    FranchiseInputComponent,
    TranslateModule,
  ],
  templateUrl: './signup.component.html',
  styleUrl: './signup.component.scss',
})
export class SignupComponent implements OnInit {
  dialog = inject(Dialog);
  snackBar = inject(MatSnackBar);
  points = SIGNUP_POINTS;
  passwordRequirements = PASSWORD_REQUIREMENTS;
  currentPasswordReqs!: IPasswordValidation[];

  protected readonly NAME_REGEX = NAME_REGEX;
  protected readonly PASTE_NAME_REGEX = PASTE_NAME_REGEX;
  protected readonly PASTE_EMAIL_REGEX = PASTE_EMAIL_REGEX;
  protected readonly EMAIL_INPUT_REGEX = EMAIL_INPUT_REGEX;
  protected readonly BUTTON_SIZE = BUTTON_SIZE;

  // @ts-ignore
  signUpForm: FormGroup;
  protected readonly ButtonTypes = BUTTON_TYPES;
  frItems: IHome_FranchisesResponse[] = [];
  selectedLocation!: string;

  isSubmitted = false;
  arePasswordRequirementsMet = false;

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private authService: AuthenticationService,
    public CONTENT: ContentService,
  ) {
  }

  get formTouched(): boolean {
    return this.signUpForm.get('givenName')?.value
      && this.signUpForm.get('familyName')?.value
      && this.signUpForm.get('email')?.value
      && this.signUpForm.get('password')?.value
      && this.signUpForm.get('confirmPassword')?.value;
  }

  ngOnInit() {
    this.getFrList();
    this.createForm();
    this.currentPasswordReqs = [...this.passwordRequirements];
  }

  createForm(): void {
    this.signUpForm = this.formBuilder.group({
      givenName: ['', [Validators.required, Validators.pattern(NAME_REGEX), notEmpty]],
      familyName: ['', [Validators.required, Validators.pattern(NAME_REGEX), notEmpty]],
      email: ['', [Validators.required, emailFormat, nonDeloitteEmail]],
      password: ['', [Validators.required]],
      code: [''],
      confirmPassword: ['', [Validators.required]],
    }, {
      validators: matchValidator('password', 'confirmPassword'),
    });
    this.validatePasswordHint();
  }

  validatePasswordHint() {
    this.signUpForm.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.currentPasswordReqs.map(requirement => {
        if (requirement.validation === 'NAME_MATCH') {
          const givenName = this.signUpForm.value.givenName.toLowerCase();
          const familyName = this.signUpForm.value.familyName.toLowerCase();
          const email = this.signUpForm.value.email.split('@')[0]?.toLowerCase();
          const password = this.signUpForm.value.password.toLowerCase();
          if (givenName.length && familyName.length && email.length && password.length) {
            requirement.valid = !password.includes(givenName) && !password.includes(familyName) && !password.includes(email);
          } else {
            requirement.valid = false;
          }
        } else {
          let regex = new RegExp(requirement.validation);
          requirement.valid = regex.test(String(this.signUpForm.value.password));
        }
      });
      this.arePasswordRequirementsMet = this.currentPasswordReqs.every(req => req.valid);
    });
  }

  getFrList(): void {
    this.CONTENT.getFranchises()
      .pipe(untilDestroyed(this))
      .subscribe(data => {
        this.frItems = data;
      });
  }

  signUp(): void {
    this.isSubmitted = true;
    if (this.signUpForm.valid) {
      let user = {
        givenName: this.signUpForm.controls['givenName'].value.trim(),
        familyName: this.signUpForm.controls['familyName'].value.trim(),
        email: this.signUpForm.controls['email'].value.toLowerCase(),
        password: this.signUpForm.controls['password'].value,
        code: '',
        name: '',
        location: this.selectedLocation,
      };
      this.authService.signUp(user)
        .then(() => {
          this.openVerificationModal();
        })
        .catch((error) => {
          this.openMessage(ToasterType.ERROR, error.message);
        });
    }
  }

  openVerificationModal(): void {
    const dialogRef = this.dialog.open<string>(VerificationModalComponent, {
      panelClass: 'custom-dialog',
      data: { email: this.signUpForm.controls['email'].value },
    });

    dialogRef.closed.pipe(take(1)).subscribe(code => {
      if (code) {
        this.confirmSignUp();
      }
    });
  }

  confirmSignUp(): void {
    this.authService.getUser()
      .then(() => {
        this.authService.signIn(
          this.signUpForm.value.email,
          this.signUpForm.value.password)
          .then(() => {
            this.authService.getUser().then(user => {
              this.authService.user.next(user);
            });
            this.router.navigate(['/en/home']);
          });
      })
      .catch((error) => {
        this.openMessage(ToasterType.ERROR, error.message);
      });
  }

  logIn(): void {
    const dialogRef = this.dialog.open(LoginModalComponent, { panelClass: 'custom-dialog' });
    dialogRef.closed.pipe(take(1)).subscribe();
  }

  selectLocation(location: IFranchiseItem) {
    this.selectedLocation = location.route;
  }

  openMessage(type: ToasterType, message: string, title?: string) {
    this.snackBar.openFromComponent(MessageToasterComponent, {
      data: {
        message: message,
        title: title,
      },
      panelClass: type,
      duration: 5000,
    });
  }
}
